import { Link } from '@afterpaytouch/core'
import clsx from 'clsx'
import { Trans } from '../../utils/convergence'
import React, { useState } from 'react'

import { AssetLayout } from '.'
import { PostCheckoutFlow } from '../../model'
import { useConsumerSelector, useMerchantDisplayNameOrName, usePostCheckoutExperiment, useSubscribeToMarketingEmailsMutation } from '../../state'
import {
  PostCaptureHeader,
  Progress,
  Subtitle,
  HeroTitle,
  PrimaryTitle,
  SecondaryTitle,
  CallToAction,
  ThankYou,
  FinePrint,
  FooterLink,
  Image,
} from './component'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'

import styles from './styles.module.css'
import { LoaderTitle } from './component/LoaderTitle'
import { MarginSize } from './component/types'
import { getImageReservedSpaceClassName } from './imageReservedSpace'

export const MarketingOptIn: AssetLayout = ({
  onSkipActionClick = () => {},
  actioned,
  assetId,
  flow,
  showProgressBar,
  disabled,
  finePrint,
  footerLinkTitle,
  onNegativeActionClick = () => {},
  onPrimaryActionClick = () => {},
  heroTitle,
  postCaptureConfirmTitle,
  postCaptureConfirmDescription,
  primaryTitle,
  privacyPolicyUrl,
  secondaryTitle,
  selectedOption,
  thankyouTitle,
  loaderTitle,
}) => {
  const { email } = useConsumerSelector()
  const merchantName = useMerchantDisplayNameOrName()
  const { value: variant } = usePostCheckoutExperiment()
  const [clickedNegative, setClickedNegative] = useState(false)
  const imageClassName = getImageReservedSpaceClassName(assetId, variant)

  const cardStyle = clsx(!clickedNegative && [styles.assetPaddedCard, styles.assetPaddingSmall])
  const [triggerSubscribeToMarketing] = useSubscribeToMarketingEmailsMutation()

  const isVariantB = variant === 'B'
  const buttonType = isVariantB ? 'radio' : 'default'
  const showImage = !isVariantB && !actioned

  const handlePrimaryActionClick = async (e: React.SyntheticEvent<HTMLInputElement, Event>): Promise<void> => {
    onPrimaryActionClick(e)
    try {
      await triggerSubscribeToMarketing({}).unwrap()
    } catch (e) {
      console.error('error subscribing to marketing emails')
    }
  }

  const handleNegativeActionClick = (e: React.SyntheticEvent<HTMLInputElement, Event>): void => {
    onNegativeActionClick(e)
    setClickedNegative(true)
  }

  return (
    <>
      <PostCaptureHeader
        title={postCaptureConfirmTitle}
        description={
          <Trans
            i18nKey={postCaptureConfirmDescription ?? ''}
            tOptions={{ email, merchantName }}
            components={{
              merchantLink: <Link onClick={onSkipActionClick} kind='Underline' />,
            }}
          />
        }
        show={flow === PostCheckoutFlow.PostCapture}
      />
      <Progress show={flow === PostCheckoutFlow.PreCapture} visible={showProgressBar} />
      <Subtitle show={!actioned} />
      <div data-testid='marketing-opt-in-card' className={cardStyle}>
        <Image show={showImage} alt={primaryTitle} hasVariantImages reservedSpaceClassName={imageClassName} />
        <MaskedElement>
          <HeroTitle show={!actioned} label={heroTitle} marginBottom={MarginSize.x1} />
        </MaskedElement>
        <div className={styles.assetTitlesPadding}>
          <PrimaryTitle show={!actioned} label={primaryTitle} testNameSpace='marketing-opt-in' />
          <SecondaryTitle show={!actioned} label={secondaryTitle} />
          <FinePrint show={!actioned} label={finePrint} privacyUrl={privacyPolicyUrl} marginTop={MarginSize.x1} />
        </div>
        <CallToAction
          show={!actioned}
          id={assetId}
          onPrimaryActionClick={handlePrimaryActionClick}
          onNegativeActionClick={handleNegativeActionClick}
          testNameSpace='postCheckout-cta'
          disabled={disabled}
          buttonType={buttonType}
        />
        <ThankYou show={actioned} selected={selectedOption} label={thankyouTitle} />
        <FooterLink
          show={!actioned}
          bold={false}
          disabled={disabled}
          onClick={handleNegativeActionClick}
          marginTop={MarginSize.x3}
          marginBottom={MarginSize.x1}
        >
          {footerLinkTitle}
        </FooterLink>
      </div>
      <LoaderTitle show={actioned} label={loaderTitle} testNameSpace='marketing-opt-in' />
    </>
  )
}
